import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  TextInput,
  Image,
  TouchableOpacity,
  FlatList,
  Keyboard,
  StyleSheet,
} from "react-native";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { AppFontStyle } from "../../styles/AppFontStyle";
import { AppImages } from "../../utility/AppImages";
import { AppColors } from "../../utility/AppColors";
import { toast } from "react-toastify";
import withErrorBoundary from "../../utility/auth/withErrorBoundary";
import { searchUsers } from "../../services/eventsAPI";
import Avatar from "../choose-avatar-components/Avatar";

type AddFriendSearchProps = {
  username: string;
  setUsername: (username: string) => void;
  onSendRequest: () => void;
  currentUsername: string;
  showBtnLoader?: false;
};

const AddFriendSearch: React.FC<AddFriendSearchProps> = ({
  username,
  setUsername,
  onSendRequest,
  currentUsername,
  showBtnLoader,
}) => {
  const styles = useStyles();
  const deviceSize = useDeviceSize();
  const [suggestions, setSuggestions] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [isUserSelected, setIsUserSelected] = useState(false);

  const callSearchAPI = async ({ bodyParams, responseSuccess }) => {
    setShowLoader(true);
    try {
      const data = await searchUsers(bodyParams);
      setShowLoader(false);

      if (data.statusCode === 200) {
        responseSuccess(data.body);
      } else {
        toast.error(data.body.error || "An error occurred");
      }
    } catch (error) {
      setShowLoader(false);
      toast.error(error.message);
    }
  };

  const fetchSuggestions = async (query: string) => {
    await callSearchAPI({
      bodyParams: { query, currentUsername },
      responseSuccess: (response) => {
        setSuggestions(response.results);
      },
    });
  };

  useEffect(() => {
    if (username && !isUserSelected) {
      fetchSuggestions(username);
    } else {
      setSuggestions([]);
    }
  }, [username, isUserSelected]);

  const handleSelect = (userID: string) => {
    setUsername(userID);
    setSuggestions([]);
    setIsUserSelected(true);
  };

  return (
    <View>
      <Text
        style={[
          deviceSize === DEVICE_SIZES.XL
            ? AppFontStyle.BOLD_ANYBODY_24
            : AppFontStyle.BOLD_ANYBODY_20,
          { flex: 1, marginTop: deviceSize === DEVICE_SIZES.XS ? 20 : 20 },
        ]}
      >
        Add Friends
      </Text>
      <View
        style={[
          styles.deviceflexDirection,
          { columnGap: 80, gap: 30, marginVertical: 20 },
        ]}
      >
        <View style={{ flexDirection: "row", columnGap: 30, marginTop: 10 }}>
          <Text style={[AppFontStyle.REGULAR_ANYBODY_20, {}]}>
            @ Add by Username
          </Text>
          <Image
            source={AppImages.arrowRight}
            style={{ height: 20, width: 20 }}
            resizeMode="contain"
          />
        </View>
        <View style={{ flexDirection: "row", gap: 10, flex: 1 }}>
          <View>
            <View style={[styles.textInputContainer]}>
              <TextInput
                placeholder={"Enter a username"}
                multiline={false}
                placeholderTextColor={AppColors.white}
                style={[
                  deviceSize === DEVICE_SIZES.XL
                    ? AppFontStyle.REGULAR_ANYBODY_20
                    : AppFontStyle.REGULAR_ANYBODY_14,
                  styles.textInputStyle,
                ]}
                keyboardType="default"
                secureTextEntry={false}
                onChangeText={(text) => {
                  setIsUserSelected(false);
                  setUsername(text);
                  if (text) {
                    fetchSuggestions(text);
                  } else {
                    setSuggestions([]);
                  }
                }}
                value={username}
                onSubmitEditing={() => {
                  Keyboard.dismiss();
                }}
                onFocus={() => {
                  if (username) {
                    fetchSuggestions(username);
                  }
                }}
              />
            </View>
            {username &&
              !showLoader &&
              suggestions.length === 0 &&
              !isUserSelected && (
                <View style={styles.dropdownContainer}>
                  <Text
                    style={[AppFontStyle.BOLD_ANYBODY_14, styles.noUserText]}
                  >
                    No user found
                  </Text>
                </View>
              )}
            {suggestions.length > 0 && !isUserSelected && (
              <View style={styles.dropdownContainer}>
                {showLoader ? (
                  <Text style={styles.loadingText}>Loading...</Text>
                ) : (
                  <FlatList
                    data={suggestions}
                    keyExtractor={(item) => item.userID}
                    renderItem={({ item }) => (
                      <TouchableOpacity
                        style={styles.suggestionItem}
                        onPress={() => handleSelect(item.userID)}
                      >
                        <View style={styles.profilePicture}>
                          <Avatar
                            src={item.profilePicture}
                            variant="pastSmallPlayer"
                          />
                        </View>
                        <Text
                          style={[AppFontStyle.BOLD_ANYBODY_14, styles.userID]}
                        >
                          {item.userID}
                        </Text>
                      </TouchableOpacity>
                    )}
                  />
                )}
              </View>
            )}
          </View>
          <TouchableOpacity
            style={styles.btnStyle}
            activeOpacity={0.5}
            onPress={onSendRequest}
            disabled={showBtnLoader}
          >
            <Text
              style={[
                deviceSize === DEVICE_SIZES.XL
                  ? AppFontStyle.BOLD_ANYBODY_16
                  : AppFontStyle.BOLD_ANYBODY_10,
                { alignSelf: "center" },
              ]}
            >
              Send Request
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

const useStyles = CreateResponsiveStyle(
  {
    textInputContainer: {
      maxWidth: 277,
      width: "auto",
      position: "relative",
      zIndex: 1,
    },
    profilePicture: {
      backgroundColor: AppColors.purpleColor_9D2CF5,
      width: 42,
      height: 42,
      borderRadius: 100,
      marginRight: 10,
      overflow: "hidden",
    },
    userID: {
      flex: 1,
      textAlignVertical: "center",
    },
    textInputStyle: {
      borderWidth: 1,
      paddingHorizontal: 10,
      paddingVertical: 0,
      borderColor: AppColors.grayColor_505050,
      backgroundColor: AppColors.grayColor_393939,
      width: "100%",
      marginRight: 20,
      height: 45,
    },
    btnStyle: {
      paddingVertical: 0,
      paddingHorizontal: 20,
      backgroundColor: AppColors.appViolentColor_6D08C3,
      shadowOpacity: 0,
      height: 45,
      borderRadius: 10,
      justifyContent: "center",
    },
    deviceflexDirection: {
      flexDirection: "row",
    },
    dropdownContainer: {
      position: "absolute",
      top: "100%",
      width: "100%",
      backgroundColor: AppColors.white,
      borderColor: AppColors.grayColor_505050,
      borderWidth: 1,
      borderRadius: 10,
      marginTop: 5,
      maxHeight: 200,
      zIndex: 1,
    },
    suggestionItem: {
      flexDirection: "row",
      alignItems: "center",
      padding: 10,
      backgroundColor: AppColors.grayColor_393939,
      borderBottomWidth: 1,
      borderBottomColor: AppColors.grayColor_505050,
    },
    suggestionText: {
      color: AppColors.white,
    },
    loadingText: {
      padding: 10,
      textAlign: "center",
    },
    noUserText: {
      padding: 10,
      textAlign: "center",
      color: AppColors.purpleColor_9D2CF5,
    },
  },
  {
    [DEVICE_SIZES.MD]: {
      textInputStyle: {
        width: "100%",
        marginRight: 0,
      },
      textInputContainer: {
        width: 200,
        position: "relative",
        zIndex: 1,
      },
      btnStyle: {
        width: "40%",
      },
      deviceflexDirection: {
        flexDirection: "column",
      },
      dropdownContainer: {
        width: "100%",
      },
    },
    [DEVICE_SIZES.SM]: {
      btnStyle: {
        flex: 1,
        width: "auto",
        paddingHorizontal: 10,
      },
    },
    [DEVICE_SIZES.XS]: {
      textInputStyle: {
        width: 200,
        marginRight: 0,
      },
      textInputContainer: {
        width: 200,
        position: "relative",
        zIndex: 1,
      },
      btnStyle: {
        flex: 1,
        width: "auto",
        paddingHorizontal: 10,
      },
      deviceflexDirection: {
        flexDirection: "column",
      },
      dropdownContainer: {
        width: "100%",
      },
    },
  }
);

export default withErrorBoundary(AddFriendSearch);
